import { Close } from "@mui/icons-material";
import { IconButton, Modal, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo } from "react";
import { useUpdatePatientTaskMutation } from "src/api/services/tasksApi";
import { Task } from "src/api/types";
import { useIsMobile } from "src/hooks/useIsMobile";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import { useTrack } from "src/hooks/useTrack";
import { desktopSx, mobileSx } from "src/utils/styles/modalStyles";

interface Props {
  title: string;
  text?: string | null;
  open: boolean;
  onClose: (o: boolean) => void;
  task: Task;
}

const MessageModal = ({ onClose, open, title, text, task }: Props) => {
  const isMobile = useIsMobile();
  const track = useTrack();
  const [completeTask] = useUpdatePatientTaskMutation();
  const errorLog = useSentryLogger("error");

  const handleClose = useCallback(async () => {
    try {
      if (task.task_type !== "WL_OPTIONS") {
        await completeTask({
          public_id: task.public_id,
          action_taken: "COMPLETED",
          completed_at: new Date().toISOString(),
        });
      }

      onClose(!open);
    } catch (err) {
      errorLog(`Error completing ${task.task_type} task`, err);
    }
  }, [completeTask, errorLog, onClose, open, task.public_id, task.task_type]);

  const eventName = useMemo(() => {
    switch (task.task_type) {
      case "DISCOUNT_OFFER":
        return "requested-discount-info";
      case "WL_OPTIONS":
        return "interested-weight-loss-options";
    }
  }, [task.task_type]);

  useEffect(() => {
    if (!open) return;
    if (eventName == null) return;

    const product = task.metadata?.product as string;

    track(eventName, {
      product,
    });

    const closeTimeout = setTimeout(() => handleClose(), 120000);

    return () => {
      clearTimeout(closeTimeout);
    };
  }, [eventName, handleClose, open, task.metadata?.product, track]);

  return (
    <Modal open={open} disableEnforceFocus>
      <Stack sx={isMobile ? mobileSx : desktopSx} gap={2} width={440}>
        <IconButton onClick={handleClose} sx={{ alignSelf: "end" }}>
          <Close />
        </IconButton>
        <Stack gap={1}>
          <Typography variant="h5" fontWeight={600} textAlign="center">
            {title}
          </Typography>
          {text != null ? (
            <Typography variant="subtitle1" textAlign="center">
              {text}
            </Typography>
          ) : null}
        </Stack>
      </Stack>
    </Modal>
  );
};

export default MessageModal;
